import { type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

interface Props {
  icon: LucideIcon;
  label: string;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}

/**
 * Single sidebar nav row. Collapsed = 60px, icon only (label as tooltip via title).
 * Expanded = 240px, icon + label.
 *
 * Active state (P2.1-S2 polished):
 * - Gradient brand bar bên trái (violet→cyan)
 * - bg `gradient-brand-soft` cho subtle highlight
 * - text-primary
 */
export function SidebarItem({
  icon: Icon,
  label,
  active,
  collapsed,
  onClick,
}: Props): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? label : undefined}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group relative w-full flex items-center gap-3 rounded-md transition-colors",
        collapsed ? "h-10 justify-center" : "h-10 px-3",
        active
          ? "bg-gradient-brand-soft text-primary"
          : "text-foreground hover:bg-muted hover:text-foreground",
      )}
    >
      {/* Active indicator bar — gradient brand */}
      {active ? (
        <span
          aria-hidden
          className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-gradient-brand"
        />
      ) : null}
      <Icon className="size-4 shrink-0" />
      {!collapsed ? (
        <span className="text-sm truncate">{label}</span>
      ) : null}
    </button>
  );
}
